import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import './assignmentModal.style.css';
import api from '../../Api';

const AgendaAssignmentModal = ({
    isOpen,
    onClose,
    onSave,
    projects,
    initialProjectId,
    initialDate
}) => {
    const { t } = useTranslation();
    const [projectId, setProjectId] = useState("");
    const [userId, setUserId] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [notes, setNotes] = useState("");
    const [error, setError] = useState("");
    const [projectUsers, setProjectUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    useEffect(() => {
        if (isOpen) {
            // Set initial values
            if (initialProjectId) {
                setProjectId(initialProjectId);
            }
            if (initialDate) {
                const dateStr = initialDate instanceof Date
                    ? initialDate.toISOString().split('T')[0]
                    : initialDate;
                setStartDate(dateStr);
                setEndDate(dateStr);
            }
        } else {
            // Reset form when closing
            setProjectId("");
            setUserId("");
            setStartDate("");
            setEndDate("");
            setNotes("");
            setError("");
            setProjectUsers([]);
        }
    }, [isOpen, initialProjectId, initialDate]);

    // Fetch users assigned to the selected project (ZZP and Voorman roles only)
    useEffect(() => {
        const fetchProjectUsers = async () => {
            if (!projectId) {
                setProjectUsers([]);
                setUserId(""); // Reset user selection when project changes
                return;
            }

            setLoadingUsers(true);
            setUserId(""); // Reset user selection when project changes

            try {
                const response = await api.get(`/projects/${projectId}/users`);
                // Filter to only show ZZP and Voorman roles
                const filteredUsers = (response.data || []).filter(user =>
                    user.project_role === 'zzper' || user.project_role === 'voorman'
                );
                setProjectUsers(filteredUsers);
            } catch (err) {
                setError(t('errors.loadUsers'));
                setProjectUsers([]);
            } finally {
                setLoadingUsers(false);
            }
        };

        fetchProjectUsers();
    }, [projectId, t]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!projectId || !userId || !startDate || !endDate) {
            setError(t('validation.required'));
            return;
        }

        if (new Date(endDate) < new Date(startDate)) {
            setError(t('errors.endDateBeforeStart'));
            return;
        }

        const assignmentData = {
            project_id: parseInt(projectId),
            user_id: parseInt(userId),
            start_date: startDate,
            end_date: endDate,
            notes: notes
        };

        await onSave(assignmentData);
    };

    if (!isOpen) return null;

    return (
        <div className="assignment-modal-overlay" onClick={onClose}>
            <div className="assignment-modal-compact" onClick={(e) => e.stopPropagation()}>
                <div className="assignment-modal-header">
                    <h2>{t('modal.assignEmployee')}</h2>
                    <button
                        className="modal-close-btn"
                        onClick={onClose}
                        aria-label={t('common.close') || 'Sluiten'}
                    >
                        ✕
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="assignment-modal-body">
                    {error && (
                        <div className="form-error-compact" role="alert">
                            {error}
                        </div>
                    )}

                    <div className="form-group-compact">
                        <label htmlFor="project-select" className="form-label-compact">
                            <span aria-hidden="true">📋</span> {t('modal.selectProject')}
                        </label>
                        <select
                            id="project-select"
                            className="form-input-compact"
                            value={projectId}
                            onChange={(e) => setProjectId(e.target.value)}
                            required
                        >
                            <option value="" disabled>{t('modal.selectProjectPlaceholder')}</option>
                            {projects.map(project => (
                                <option key={project.id} value={project.id}>
                                    {project.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group-compact">
                        <label htmlFor="user-select" className="form-label-compact">
                            <span aria-hidden="true">👤</span> {t('modal.selectEmployee')}
                        </label>
                        <select
                            id="user-select"
                            className="form-input-compact"
                            value={userId}
                            onChange={(e) => setUserId(e.target.value)}
                            required
                            disabled={!projectId || loadingUsers}
                        >
                            <option value="" disabled>
                                {!projectId
                                    ? t('modal.selectProjectFirst')
                                    : loadingUsers
                                    ? t('common.loading')
                                    : projectUsers.length === 0
                                    ? t('agenda.noEmployeesAssigned')
                                    : t('modal.selectEmployeePlaceholder')}
                            </option>
                            {projectUsers.map(user => (
                                <option key={user.user_id} value={user.user_id}>
                                    {user.user_name} ({user.project_role === 'zzper' ? 'ZZP' : 'Voorman'})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-row-compact">
                        <div className="form-group-compact">
                            <label htmlFor="start-date" className="form-label-compact">
                                <span aria-hidden="true">📅</span> {t('modal.startDate')}
                            </label>
                            <input
                                type="date"
                                id="start-date"
                                className="form-input-compact"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                required
                            />
                        </div>

                        <div className="form-group-compact">
                            <label htmlFor="end-date" className="form-label-compact">
                                <span aria-hidden="true">📅</span> {t('modal.endDate')}
                            </label>
                            <input
                                type="date"
                                id="end-date"
                                className="form-input-compact"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group-compact">
                        <label htmlFor="notes" className="form-label-compact">
                            <span aria-hidden="true">📝</span> {t('modal.notes')} <span className="optional-label">({t('common.optional')})</span>
                        </label>
                        <textarea
                            id="notes"
                            className="form-input-compact"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows="2"
                            placeholder={t('modal.notesPlaceholder')}
                        />
                    </div>

                    <div className="modal-actions-compact">
                        <button type="button" className="btn btn-cancel" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                        <button type="submit" className="btn btn-save">
                            <span aria-hidden="true">💾</span> {t('common.save')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

AgendaAssignmentModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    projects: PropTypes.array.isRequired,
    initialProjectId: PropTypes.number,
    initialDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])
};

export default AgendaAssignmentModal;

